import {
  FontLoadingStatus,
  FontLoadingResult,
} from '@contentlab/api/fonts/types';
import {fontsStoreActions} from '@contentlab/stores/fontStore';
import {FontVariants} from '@contentlab/types/captions';

class FontDownloader {
  static async loadFont(
    fontId: string,
    variant: FontVariants
  ): Promise<FontLoadingResult> {
    if (fontsStoreActions.isFontLoaded(fontId, variant)) {
      return {status: FontLoadingStatus.LOADED};
    }

    const metadata = fontsStoreActions.getFontMetadata(fontId);
    if (!metadata) {
      return {
        status: FontLoadingStatus.ERROR,
        error: `Font metadata not found for ID: ${fontId}`,
      };
    }

    const downloadUrl = metadata.downloadUrls[variant];
    if (!downloadUrl) {
      return {
        status: FontLoadingStatus.ERROR,
        error: `Download URL not found for font ${fontId}, variant ${variant}`,
      };
    }

    try {
      await FontDownloader.loadFontFromUrl(downloadUrl, metadata.name, variant);
      fontsStoreActions.setFontLoaded(fontId, variant);
      return {status: FontLoadingStatus.LOADED};
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load font variant';
      fontsStoreActions.setFontError(fontId, variant, errorMessage);
      return {
        status: FontLoadingStatus.ERROR,
        error: errorMessage,
      };
    }
  }

  /**
   * Load font from URL using CSS and wait for font to be actually ready
   */
  private static async loadFontFromUrl(
    url: string,
    fontFamily: string,
    variant: FontVariants
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingLink = document.querySelector(`link[href="${url}"]`);
      if (existingLink) {
        FontDownloader.waitForFontToBeReady(fontFamily, variant)
          .then(resolve)
          .catch(reject);
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;

      link.onload = async () => {
        try {
          await FontDownloader.waitForFontToBeReady(fontFamily, variant);
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      link.onerror = () => {
        reject(new Error(`Failed to load font stylesheet from ${url}`));
      };

      document.head.appendChild(link);
    });
  }

  /**
   * Wait for font to actually be ready using Font Loading API
   */
  private static async waitForFontToBeReady(
    fontFamily: string,
    variant: FontVariants
  ): Promise<void> {
    const {style: fontStyle} = FontDownloader.getFontStyleProperties(variant);

    const fontDescriptor = `${fontStyle} 16px "${fontFamily}"`;

    try {
      if ('fonts' in document) {
        await document.fonts.load(fontDescriptor);

        if (!document.fonts.check(fontDescriptor)) {
          throw new Error(`Font not available: ${fontDescriptor}`);
        }
      } else {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 100);
        });
      }
    } catch (error) {
      throw new Error(
        `Failed to load font "${fontFamily}" ${variant}: ${error}`
      );
    }
  }

  /**
   * Get font style properties for a variant using CSS keywords
   */
  private static getFontStyleProperties(variant: FontVariants): {
    style: string;
  } {
    switch (variant) {
      case FontVariants.REGULAR:
        return {style: 'normal'};
      case FontVariants.BOLD:
        return {style: 'normal'};
      case FontVariants.ITALIC:
        return {style: 'italic'};
      default:
        return {style: 'normal'};
    }
  }

  static clearCache(): void {
    fontsStoreActions.clearFontsMetadata();
  }
}

export {FontDownloader};
export const fontDownloader = new FontDownloader();
export default fontDownloader;
